/**
 * Parent Company Management Component
 * CRUD operations for parent companies
 */
import { useState, useEffect } from 'react';
import api from '../utils/api';

function ParentCompanyManagement() {
  const [parentCompanies, setParentCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedParentCompany, setSelectedParentCompany] = useState(null);
  const [formData, setFormData] = useState({
    parent_company_id: '',
    parent_company_name: '',
    description: '',
  });

  useEffect(() => {
    loadParentCompanies();
  }, []);

  const loadParentCompanies = async () => {
    try {
      const response = await api.get('/parent-companies');
      setParentCompanies(response.data);
    } catch (error) {
      console.error('Failed to load parent companies:', error);
      alert('Failed to load parent companies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/parent-companies', formData);
      setShowCreateForm(false);
      setFormData({ parent_company_id: '', parent_company_name: '', description: '' });
      loadParentCompanies();
      alert('Parent company created successfully');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create parent company');
    }
  };

  const handleEdit = (parentCompany) => {
    setSelectedParentCompany(parentCompany);
    setFormData({
      parent_company_id: parentCompany.parent_company_id,
      parent_company_name: parentCompany.parent_company_name,
      description: parentCompany.description || '',
    });
    setShowEditForm(true);
    setShowCreateForm(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        parent_company_name: formData.parent_company_name,
        description: formData.description || null,
      };

      await api.put(`/parent-companies/${selectedParentCompany.parent_company_id}`, updateData);
      setShowEditForm(false);
      setSelectedParentCompany(null);
      setFormData({ parent_company_id: '', parent_company_name: '', description: '' });
      loadParentCompanies();
      alert('Parent company updated successfully');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update parent company');
    }
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setSelectedParentCompany(null);
    setFormData({ parent_company_id: '', parent_company_name: '', description: '' });
  };

  const handleDelete = async (parentCompanyId) => {
    if (!confirm(`Are you sure you want to delete parent company ${parentCompanyId}?`)) return;

    try {
      await api.delete(`/parent-companies/${parentCompanyId}`);
      loadParentCompanies();
      alert('Parent company deleted successfully');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete parent company');
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="parent-company-management">
      <div className="management-header">
        <h2>Parent Companies</h2>
        <button onClick={() => setShowCreateForm(!showCreateForm)} className="btn-primary">
          {showCreateForm ? 'Cancel' : '+ Add Parent Company'}
        </button>
      </div>

      {showCreateForm && (
        <form onSubmit={handleCreate} className="create-form">
          <h3>Add New Parent Company</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Parent Company Name</label>
              <input
                type="text"
                value={formData.parent_company_name}
                onChange={(e) => setFormData({ ...formData, parent_company_name: e.target.value })}
                required
                placeholder="Acme Corporation"
              />
            </div>
            <div className="form-group">
              <label>Parent Company ID (optional)</label>
              <input
                type="text"
                value={formData.parent_company_id}
                onChange={(e) => setFormData({ ...formData, parent_company_id: e.target.value })}
                placeholder="Leave empty to auto-generate from name"
              />
              <small style={{ color: '#666' }}>Auto-generated from name if left empty</small>
            </div>
            <div className="form-group full-width">
              <label>Description (optional)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the parent company"
                rows={3}
              />
            </div>
          </div>
          <button type="submit" className="btn-primary">Add Parent Company</button>
        </form>
      )}

      {showEditForm && (
        <form onSubmit={handleUpdate} className="create-form">
          <h3>Edit Parent Company: {selectedParentCompany?.parent_company_name}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Parent Company ID</label>
              <input
                type="text"
                value={formData.parent_company_id}
                disabled
                className="disabled-input"
              />
              <small style={{ color: '#666' }}>Parent Company ID cannot be changed</small>
            </div>
            <div className="form-group">
              <label>Parent Company Name</label>
              <input
                type="text"
                value={formData.parent_company_name}
                onChange={(e) => setFormData({ ...formData, parent_company_name: e.target.value })}
                required
                placeholder="Acme Corporation"
              />
            </div>
            <div className="form-group full-width">
              <label>Description (optional)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the parent company"
                rows={3}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" className="btn-primary">Update Parent Company</button>
            <button type="button" onClick={handleCancelEdit} className="btn-secondary">Cancel</button>
          </div>
        </form>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>Parent Company ID</th>
            <th>Parent Company Name</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {parentCompanies.map((parentCompany) => (
            <tr key={parentCompany.parent_company_id}>
              <td>{parentCompany.parent_company_id}</td>
              <td>{parentCompany.parent_company_name}</td>
              <td>{parentCompany.description || '-'}</td>
              <td>
                <button
                  onClick={() => handleEdit(parentCompany)}
                  className="btn-small"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(parentCompany.parent_company_id)}
                  className="btn-small btn-danger"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ParentCompanyManagement;
